import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Col, ProgressBar, Row, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '@services/api'; // Assurez-vous que le chemin est correct

const StatsTab = ({ meetingsData, salonId }) => {
  const [isExporting, setIsExporting] = useState(false);
  // Calculs existants
  const totalCompanyWishes = meetingsData.company_stats?.reduce(
    (acc, s) => acc + s.total_wishes,
    0
  );
  const metCompanyWishes = meetingsData.company_stats?.reduce(
    (acc, s) => acc + s.met_wishes,
    0
  );
  const companySatisfactionRate =
    totalCompanyWishes > 0 ? (metCompanyWishes / totalCompanyWishes) * 100 : 0;

  const totalPartnerWishes = meetingsData.partner_stats?.reduce(
    (acc, s) => acc + s.total_wishes,
    0
  );
  const metPartnerWishes = meetingsData.partner_stats?.reduce(
    (acc, s) => acc + s.met_wishes,
    0
  );
  const partnerSatisfactionRate =
    totalPartnerWishes > 0 ? (metPartnerWishes / totalPartnerWishes) * 100 : 0;

  // NOUVEAU CALCUL
  const totalMeetings = meetingsData.meetings?.length || 0;

  // Tri par ordre alphabétique
  const sortedCompanyStats = [...(meetingsData.company_stats || [])].sort(
    (a, b) => a.name.localeCompare(b.name)
  );
  const sortedPartnerStats = [...(meetingsData.partner_stats || [])].sort(
    (a, b) => a.name.localeCompare(b.name)
  );

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // On attend une réponse JSON, donc on retire responseType: 'blob'
      const response = await api.get(
        `/admin/partner-salons/${salonId}/export-stats`
      );

      if (response.data.success) {
        // Décoder la chaîne Base64 en données binaires
        const byteCharacters = atob(response.data.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        // Créer le lien de téléchargement
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', response.data.file_name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">Statistiques du salon</h5>
        <Button
          variant="outline-success"
          size="sm"
          onClick={handleExport}
          disabled={isExporting}
        >
          <FontAwesomeIcon
            icon={isExporting ? faSpinner : faFileExcel}
            spin={isExporting}
            className="me-2"
          />
          {isExporting ? 'Export en cours...' : 'Exporter en Excel'}
        </Button>
      </div>
      {/* Cartes globales modifiées pour 3 colonnes */}
      <Row>
        <Col md={4} className="mb-4">
          <Card className="h-100">
            <Card.Header>
              <h6 className="mb-0">Rendez-vous générés</h6>
            </Card.Header>
            <Card.Body className="text-center">
              <h3 className="fw-bold">{totalMeetings}</h3>
              <p className="mb-0 text-muted">Total des rendez-vous planifiés</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-4">
          <Card className="h-100">
            <Card.Header>
              <h6 className="mb-0">Taux de satisfaction - Entreprises</h6>
            </Card.Header>
            <Card.Body className="text-center">
              <h3 className="fw-bold">{companySatisfactionRate.toFixed(1)}%</h3>
              <ProgressBar
                now={companySatisfactionRate}
                label={`${companySatisfactionRate.toFixed(1)}%`}
                visuallyHidden
                className="mb-2"
              />
              <p className="mb-0 text-muted">
                {metCompanyWishes} vœux respectés sur {totalCompanyWishes}
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-4">
          <Card className="h-100">
            <Card.Header>
              <h6 className="mb-0">Taux de satisfaction - Partenaires</h6>
            </Card.Header>
            <Card.Body className="text-center">
              <h3 className="fw-bold">{partnerSatisfactionRate.toFixed(1)}%</h3>
              <ProgressBar
                now={partnerSatisfactionRate}
                label={`${partnerSatisfactionRate.toFixed(1)}%`}
                visuallyHidden
                className="mb-2"
              />
              <p className="mb-0 text-muted">
                {metPartnerWishes} vœux respectés sur {totalPartnerWishes}
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tableaux de stats détaillées (inchangés) */}
      <Row>
        <Col md={6}>
          <h6>Détail par entreprise</h6>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Entreprise</th>
                <th className="text-center">Vœux</th>
                <th className="text-center">Satisfaits</th>
                <th className="text-center">Taux</th>
              </tr>
            </thead>
            <tbody>
              {sortedCompanyStats.map(stat => (
                <tr key={`cs-${stat.id}`}>
                  <td>{stat.name}</td>
                  <td className="text-center">{stat.total_wishes}</td>
                  <td className="text-center">{stat.met_wishes}</td>
                  <td className="text-center">
                    {stat.satisfaction_rate.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
        <Col md={6}>
          <h6>Détail par partenaire</h6>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Partenaire</th>
                <th className="text-center">Vœux</th>
                <th className="text-center">Satisfaits</th>
                <th className="text-center">Taux</th>
              </tr>
            </thead>
            <tbody>
              {sortedPartnerStats.map(stat => (
                <tr key={`ps-${stat.id}`}>
                  <td>{stat.name}</td>
                  <td className="text-center">{stat.total_wishes}</td>
                  <td className="text-center">{stat.met_wishes}</td>
                  <td className="text-center">
                    {stat.satisfaction_rate.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    </div>
  );
};

StatsTab.propTypes = {
  meetingsData: PropTypes.shape({
    meetings: PropTypes.array, // Ajout de la prop 'meetings'
    company_participants: PropTypes.array,
    partner_participants: PropTypes.array,
    unmet_company_wishes: PropTypes.array,
    unmet_partner_wishes: PropTypes.array,
    company_stats: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        total_wishes: PropTypes.number,
        met_wishes: PropTypes.number,
        satisfaction_rate: PropTypes.number
      })
    ),
    partner_stats: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        total_wishes: PropTypes.number,
        met_wishes: PropTypes.number,
        satisfaction_rate: PropTypes.number
      })
    )
  }).isRequired,
  salonId: PropTypes.number.isRequired
};

export default StatsTab;
